import axios from "axios";
import type { AxiosError, AxiosRequestConfig } from "axios";

const baseURL = (import.meta.env.VITE_API_BASE as string | undefined) || "/api";

export const api = axios.create({ baseURL, timeout: 30_000 });

const ACCESS_KEY = "videotool.access";
const REFRESH_KEY = "videotool.refresh";

export function setTokens(access: string | null, refresh?: string | null) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  else localStorage.removeItem(ACCESS_KEY);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  if (refresh === null) localStorage.removeItem(REFRESH_KEY);
}

export function getAccess(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
export function getRefresh(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

// Endpoints públicos: NO adjuntar Bearer. Si el access caducó en localStorage,
// un Bearer inválido haría que DRF (JWTAuthentication) devuelva 401 antes incluso
// de comprobar el AllowAny de la vista, rompiendo flujos de login/invite/refresh.
const PUBLIC_PATHS = [
  "/auth/login/",
  "/auth/refresh/",
  "/auth/accept-invitation/",
  "/invitations/check/",
];
function isPublicUrl(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_PATHS.some((p) => url.startsWith(p) || url.includes(p));
}

api.interceptors.request.use((cfg) => {
  if (isPublicUrl(cfg.url)) return cfg;
  const t = getAccess();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let refreshing: Promise<string | null> | null = null;

async function refreshAccess(): Promise<string | null> {
  const r = getRefresh();
  if (!r) return null;
  try {
    const resp = await axios.post(`${baseURL}/auth/refresh/`, { refresh: r });
    const access = resp.data.access as string;
    const refresh = resp.data.refresh as string | undefined;
    setTokens(access, refresh || r);
    return access;
  } catch {
    setTokens(null, null);
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as AxiosRequestConfig & { _retry?: boolean };
    // En endpoints públicos un 401 lo gestiona la página (p. ej. AcceptInvitePage
    // muestra "no válida"). No intentar refresh ni redirigir a /login.
    if (isPublicUrl(original?.url)) return Promise.reject(err);
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      refreshing = refreshing || refreshAccess();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return api.request(original);
      }
      if (typeof window !== "undefined" && !location.pathname.startsWith("/login")) {
        location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);
